import * as React from 'react'
import { FC } from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import GestureIcon from '@mui/icons-material/Gesture'

type Props = {
  onCancel: () => void
  /** Set when the last attempt could not become a territory; replaces the usual instructions. */
  hint?: string | null
  /**
   * The headline above `hint`. Needed because not every failed stroke is a crossed loop: a line
   * that misses the territory fails differently and must not be described as one that did not.
   */
  hintTitle?: string | null
  /**
   * Replaces the default "draw around the area" wording. Splitting asks for a different gesture
   * on the same map, and a banner that still says "draw a loop" while the user is meant to draw
   * a line is worse than no banner.
   */
  instruction?: { title: string; detail: string } | null
  /**
   * An offer to switch gesture, shown next to Exit. Present only where there genuinely are two
   * ways to do the thing -- a split can be a line across or a piece carved out.
   */
  altAction?: { label: string; onClick: () => void } | null
  /**
   * The main thing to do next, in the rightmost slot where Exit otherwise sits.
   *
   * Used when the banner is up but the pen is NOT armed -- arriving from an assignment rule,
   * where finding the area comes first and "Start drawing" is the whole point of the screen.
   * Exit steps left to make room, rather than being the most prominent button on a bar whose
   * purpose is to start something.
   */
  primaryAction?: { label: string; onClick: () => void } | null
}

/**
 * Tells the admin what to do while draw mode is on (WWM-17508, Feature A).
 *
 * Draw mode disables panning, so without this the map silently stops behaving like a map. The
 * wording is deliberately plain -- the people drawing territories are sales admins, so it says
 * what to do with a finger or a mouse, not "digitise a polygon".
 */
/** Keeps the banner readable on a narrow phone without overlapping the map controls. */
const WIDTH_LEAVING_ROOM_FOR_MAP_CONTROLS = 'calc(100% - 32px)'

export const TerritoryDrawBanner: FC<Props> = ({
  onCancel,
  hint,
  hintTitle,
  instruction,
  altAction,
  primaryAction,
}) => (
  <Paper
    elevation={6}
    sx={{
      position: 'absolute',
      top: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 2,
      px: 2,
      py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      maxWidth: WIDTH_LEAVING_ROOM_FOR_MAP_CONTROLS,
    }}
    data-test-id="territory-draw-banner"
  >
    <GestureIcon fontSize="small" color={hint ? 'warning' : 'primary'} />

    <Box>
      <Typography variant="body2" fontWeight={600}>
        {hint
          ? // Neutral, because this is now only reached by a caller that raised a hint without
            // saying what it was about. It used to read "That shape crossed over itself", which
            // was a confident guess and wrong for every hint except one.
            (hintTitle ?? 'That did not work \u2014 try again')
          : (instruction?.title ?? 'Draw around the area you want')}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hint ??
          instruction?.detail ??
          'Hold down and drag a loop on the map. Let go to finish.'}
      </Typography>
    </Box>

    {altAction && (
      <Button
        size="small"
        onClick={altAction.onClick}
        data-test-id="territory-draw-alt-mode"
        sx={{ flexShrink: 0 }}
      >
        {altAction.label}
      </Button>
    )}

    {/*
      "Exit drawing", never "Cancel". When this bar is showing a warning it reads as a message
      box, and a Cancel on a message box means "dismiss this message" -- a user pressed it
      expecting exactly that and was dropped out of draw mode. The label now says what it does.
      The warning itself needs no dismiss button: it clears as soon as the next stroke starts.
    */}
    <Button
      size="small"
      onClick={onCancel}
      data-test-id="territory-draw-cancel"
      sx={{ flexShrink: 0 }}
    >
      Exit drawing
    </Button>

    {primaryAction && (
      <Button
        size="small"
        variant="contained"
        onClick={primaryAction.onClick}
        data-test-id="territory-draw-primary"
        sx={{ flexShrink: 0 }}
      >
        {primaryAction.label}
      </Button>
    )}
  </Paper>
)
